/**
 * About Us Page
 * Route: /about
 * Features: Premium hero banner, statistics grid, "Our Story" layout, Core Values grid, Call-To-Action (CTA) section
 */

import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/common/SEO';
import {
  ArrowLeft,
  ShieldCheck,
  Users,
  Target,
  Sparkles,
  Award,
  Lock,
  ArrowRight,
} from 'lucide-react';

const AboutPage: React.FC = () => {
  return (
    <div className="about-page" id="about-page">
      <SEO
        title="About Us"
        description="Learn more about MemAds. We are building India's most trusted local marketplace to empower local trade with safety and convenience."
        canonical="/about"
      />
      {/* Hero Header */}
      <section className="about-hero">
        <div className="about-hero__pattern" />
        <div className="about-hero__inner">
          <Link to="/" className="about-hero__back">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
          <h1 className="about-hero__title">About MemAds</h1>
          <p className="about-hero__subtitle">
            Empowering local trade and transforming classifieds — we're building India's most trusted local marketplace.
          </p>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="about-stats-container">
        <div className="about-stats-grid">
          <div className="about-stat-card">
            <div className="about-stat-card__number">50K+</div>
            <div className="about-stat-card__label">Active Listings</div>
          </div>
          <div className="about-stat-card">
            <div className="about-stat-card__number">10K+</div>
            <div className="about-stat-card__label">Registered Sellers</div>
          </div>
          <div className="about-stat-card">
            <div className="about-stat-card__number">500K+</div>
            <div className="about-stat-card__label">Happy Buyers</div>
          </div>
          <div className="about-stat-card">
            <div className="about-stat-card__number">200+</div>
            <div className="about-stat-card__label">Local Communities</div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="about-story-section">
        <div className="about-story-inner">
          <div className="about-story-content">
            <span className="about-section-tag">Our Journey</span>
            <h2 className="about-section-title">Our Story</h2>
            <div className="about-story-text">
              <p>
                MemAds was founded with a simple yet powerful vision: to make local buying and selling effortless, transparent, and rewarding for everyone. We noticed that the local marketplace landscape was fragmented and overwhelming — both for buyers searching for quality items and for sellers struggling to reach their local community effectively.
              </p>
              <p>
                Today, MemAds connects millions of buyers with thousands of trusted sellers across India. Our platform combines intelligent local matching, a seamless user experience, and robust seller verification tools to create secure, local marketplace connections every day.
              </p>
              <p>
                Whether you're looking to declutter your home, buy pre-owned electronics, find a new apartment, or grow your local business, MemAds is here to help you trade safely and build a thriving local economy.
              </p>
            </div>
          </div>
          <div className="about-story-media">
            <div className="about-image-wrapper">
              <img
                src="https://picsum.photos/seed/memads-story/600/450"
                alt="Our Story"
                className="about-story-image"
              />
              <div className="about-image-overlay" />
            </div>
          </div>
        </div>
      </section>

      {/* What We Stand For (Core Values) */}
      <section className="about-values-section">
        <div className="about-values-inner">
          <div className="about-values-header">
            <span className="about-section-tag">Principles</span>
            <h2 className="about-section-title text-center mx-auto">What We Stand For</h2>
            <p className="about-section-subtitle text-center mx-auto">
              Our core values guide every decision we make and every feature we build to serve our users better.
            </p>
          </div>

          <div className="about-values-grid">
            {/* Mission */}
            <div className="about-value-card">
              <div className="about-value-card__icon bg-primary/10 text-primary">
                <Target className="h-6 w-6" />
              </div>
              <h3 className="about-value-card__title">Our Mission</h3>
              <p className="about-value-card__desc">
                To bridge the gap between local buyers and sellers, making the transaction process seamless, efficient, and rewarding.
              </p>
            </div>

            {/* Community First */}
            <div className="about-value-card">
              <div className="about-value-card__icon bg-green-500/10 text-green-500">
                <Users className="h-6 w-6" />
              </div>
              <h3 className="about-value-card__title">Community First</h3>
              <p className="about-value-card__desc">
                We believe in putting our local communities at the center of everything we do. Every feature is designed to empower local trade.
              </p>
            </div>

            {/* Excellence */}
            <div className="about-value-card">
              <div className="about-value-card__icon bg-purple-500/10 text-purple-500">
                <Award className="h-6 w-6" />
              </div>
              <h3 className="about-value-card__title">Excellence</h3>
              <p className="about-value-card__desc">
                We strive for excellence in every aspect of our platform — from user experience and design to search relevance and interface speed.
              </p>
            </div>

            {/* Innovation */}
            <div className="about-value-card">
              <div className="about-value-card__icon bg-blue-500/10 text-blue-500">
                <Sparkles className="h-6 w-6" />
              </div>
              <h3 className="about-value-card__title">Innovation</h3>
              <p className="about-value-card__desc">
                We leverage cutting-edge technology and location-based insights to continuously improve how users find what they need.
              </p>
            </div>

            {/* Trust & Integrity */}
            <div className="about-value-card">
              <div className="about-value-card__icon bg-amber-500/10 text-amber-500">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <h3 className="about-value-card__title">Trust & Integrity</h3>
              <p className="about-value-card__desc">
                We maintain the highest standards of safety, verified seller checks, and privacy to earn and keep the trust of our users.
              </p>
            </div>

            {/* Secure Trade */}
            <div className="about-value-card">
              <div className="about-value-card__icon bg-rose-500/10 text-rose-500">
                <Lock className="h-6 w-6" />
              </div>
              <h3 className="about-value-card__title">Secure Trade</h3>
              <p className="about-value-card__desc">
                We offer verified seller badges, end-to-end messaging encryption, and reporting tools to ensure a completely safe marketplace experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <div className="about-cta__inner">
          <h2 className="about-cta__title font-heading">Ready to Start Trading?</h2>
          <p className="about-cta__subtitle">
            Join millions of buyers and sellers who trust MemAds for local marketplace transactions.
          </p>
          <div className="about-cta__actions">
            <Link to="/listings" className="about-cta__btn-primary">
              Browse Listings
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/post" className="about-cta__btn-secondary">
              Post an Ad
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
